// src/lib/roles.ts
// The role vocabulary, in one place, because there are TWO spellings of the
// same role and they disagreed.
//
// `profiles.role` is CHECKed against
//   ('standard', 'executive', 'super', 'super_admin', 'viewer')
// but the UI typed it as only ('standard' | 'executive' | 'super') and compared
// against the bare 'super'. Nothing in the table has ever been 'super': every
// real super admin is 'super_admin', which is also what ecg_is_super_admin()
// and 25 RLS policies read. So the UI's super check could not pass for anyone,
// and `viewer` / `super_admin` fell through the permission table into "no
// permissions at all".
//
// The database is the side that is right — migrating the data to 'super' would
// have to move 25 policies with it — so the vocabulary widens here to match,
// and both spellings are accepted wherever "is a super admin" is asked.

/** Every value `profiles.role` is allowed to hold. */
export type ProfileRole = 'viewer' | 'standard' | 'executive' | 'super' | 'super_admin';

/** The two spellings that mean "super admin". */
export const SUPER_ROLES: ReadonlySet<string> = new Set(['super', 'super_admin']);

/** True for either spelling of super admin, and nothing else. */
export function isSuperRole(role: string | null | undefined): boolean {
  return SUPER_ROLES.has(role ?? '');
}

const STANDARD: readonly string[] = [
    'view:properties',
    'create:properties',
    'edit:properties',
    'view:contacts',
    'create:contacts',
    'edit:contacts',
    'view:tasks',
    'create:tasks',
    'edit:tasks',
    'view:diligence',
    'edit:diligence',
    'view:communications',
    'create:communications',
    'view:sources',
    'create:sources',
    'edit:sources',
    'view:markets',
    'view:comps',
    'create:comps',
];

const EXECUTIVE: readonly string[] = [
    // All standard permissions
    'view:properties',
    'create:properties',
    'edit:properties',
    'delete:properties',
    'view:contacts',
    'create:contacts',
    'edit:contacts',
    'view:tasks',
    'create:tasks',
    'edit:tasks',
    'view:diligence',
    'edit:diligence',
    'view:communications',
    'create:communications',
    'view:sources',
    'create:sources',
    'edit:sources',
    'view:markets',
    'edit:markets',
    'view:comps',
    'create:comps',
    // Executive-only permissions
    'view:analysis',
    'create:analysis',
    'edit:analysis',
    'export:reports',
    'view:offers',
    'create:offers',
    'edit:offers',
    'view:financing',
    'create:financing',
    'edit:financing',
    'view:ic',
    'create:ic',
    'edit:ic',
    'view:analytics',
    'manage:criteria',
];

export const ROLE_PERMISSIONS: Record<ProfileRole, readonly string[]> = {
  // A viewer is read-gated by RLS, not by this table; the empty list is what
  // the old `PERMISSIONS[role] || []` fallback already produced for them, so
  // spelling it out changes nothing and makes the record total.
  viewer: [],
  standard: STANDARD,
  executive: EXECUTIVE,
  super: ['*'],
  super_admin: ['*'],
};

/** Whether a role grants an action. Unknown or missing roles grant nothing. */
export function roleCan(role: string | null | undefined, action: string): boolean {
  const granted = ROLE_PERMISSIONS[(role ?? '') as ProfileRole];
  if (!granted) return false;
  return granted.includes('*') || granted.includes(action);
}

/**
 * Rank, for "at least this role" checks. Both super spellings share the top
 * rank; an unknown role sits below everything rather than indexing to
 * `undefined` and comparing false by accident.
 */
export const ROLE_LEVELS: Record<ProfileRole, number> = {
  viewer: 0,
  standard: 1,
  executive: 2,
  super: 3,
  super_admin: 3,
};

export function roleAtLeast(
  role: string | null | undefined,
  minimum: ProfileRole,
): boolean {
  const have = ROLE_LEVELS[(role ?? '') as ProfileRole];
  const need = ROLE_LEVELS[minimum];
  if (have === undefined || need === undefined) return false;
  return have >= need;
}
